import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

// Database types
interface DbDisciplina {
  id: string;
  user_id: string;
  nome: string;
  sigla: string | null;
  ativo: boolean;
  tempo_ciclo_min: number;
  criado_em: string;
}

interface DbConteudo {
  id: string;
  disciplina_id: string;
  nome: string;
  descricao: string | null;
  revisao_concluida: boolean;
  criado_em: string;
}

interface DbSessaoEstudo {
  id: string;
  user_id: string;
  disciplina_id: string;
  conteudo_id: string | null;
  tempo_min: number;
  data: string;
  questoes_certas: number | null;
  questoes_erradas: number | null;
  questoes_branco: number | null;
  observacao: string | null;
  tipo_estudo: string;
  pagina_inicio: number | null;
  pagina_fim: number | null;
  video_url: string | null;
  video_minuto_inicio: number | null;
  video_minuto_fim: number | null;
  criado_em: string;
}

interface DbTipoEstudo {
  id: string;
  user_id: string;
  nome: string;
  criado_em: string;
}

export interface TipoEstudo {
  id: string;
  nome: string;
  isDefault?: boolean;
}

interface DbRevisao {
  id: string;
  conteudo_id: string;
  concluida: boolean;
  data_conclusao: string | null;
  data_agendada: string;
  sessao_id: string | null;
  criado_em: string;
}

interface DbProfile {
  id: string;
  user_id: string;
  objetivo_geral: string | null;
  nome_completo: string | null;
  criado_em: string;
}

interface DbSimulado {
  id: string;
  user_id: string;
  titulo: string;
  banca: string | null;
  tipo_questao: string;
  tipo_pontuacao: string;
  questoes_certas: number;
  questoes_erradas: number;
  questoes_branco: number;
  nota_final: number | null;
  data: string;
  observacao: string | null;
  criado_em: string;
}

// Date helpers
// IMPORTANT: for Postgres DATE (YYYY-MM-DD), we must parse as a *local* date (not UTC)
// to avoid off-by-one issues when formatting/comparing.
const parseDateOnly = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// App types
export interface Disciplina {
  id: string;
  userId: string;
  nome: string;
  sigla?: string;
  ativo: boolean;
  tempoCicloMin: number;
  criadoEm: Date;
}

export interface Conteudo {
  id: string;
  disciplinaId: string;
  nome: string;
  descricao?: string;
  revisaoConcluida: boolean;
  criadoEm: Date;
}

export interface Revisao {
  id: string;
  conteudoId: string;
  concluida: boolean;
  dataConclusao?: Date;
  dataAgendada: Date;
  sessaoId?: string;
  criadoEm: Date;
}

export interface SessaoEstudo {
  id: string;
  userId: string;
  disciplinaId: string;
  conteudoId?: string;
  tempoMin: number;
  data: Date;
  tipoEstudo: string;
  questoesCertas?: number;
  questoesErradas?: number;
  questoesBranco?: number;
  paginaInicio?: number;
  paginaFim?: number;
  observacao?: string;
  videoUrl?: string;
  videoMinutoInicio?: number;
  videoMinutoFim?: number;
  criadoEm: Date;
}

export interface Simulado {
  id: string;
  userId: string;
  titulo: string;
  banca: string | null;
  tipoQuestao: string;
  tipoPontuacao: string;
  questoesCertas: number;
  questoesErradas: number;
  questoesBranco: number;
  notaFinal: number | null;
  data: Date;
  observacao: string | null;
  criadoEm: Date;
}

interface AppState {
  objetivoGeral: string;
  nomeCompleto: string;
  disciplinas: Disciplina[];
  conteudos: Conteudo[];
  sessoes: SessaoEstudo[];
  revisoes: Revisao[];
  simulados: Simulado[];
  tiposEstudo: TipoEstudo[];
  loading: boolean;
}

const DEFAULT_TIPOS_ESTUDO: TipoEstudo[] = [
  { id: 'estudo_teorico', nome: 'Estudo Teórico', isDefault: true },
  { id: 'exercicios', nome: 'Exercícios', isDefault: true },
  { id: 'video_aula', nome: 'Vídeo Aula', isDefault: true },
  { id: 'simulado', nome: 'Simulado', isDefault: true },
];

interface AppContextType extends AppState {
  setObjetivoGeral: (objetivo: string) => Promise<void>;
  setNomeCompleto: (nome: string) => Promise<void>;
  addDisciplina: (nome: string, sigla?: string, tempoCicloMin?: number) => Promise<void>;
  updateDisciplina: (id: string, nome: string, sigla?: string, tempoCicloMin?: number) => Promise<void>;
  deleteDisciplina: (id: string) => Promise<void>;
  toggleDisciplinaAtivo: (id: string) => Promise<void>;
  addConteudo: (disciplinaId: string, nome: string, descricao?: string) => Promise<void>;
  updateConteudo: (id: string, nome: string, descricao?: string) => Promise<void>;
  deleteConteudo: (id: string) => Promise<void>;
  toggleConteudoConcluido: (id: string) => Promise<void>;
  addSessao: (sessao: Omit<SessaoEstudo, 'id' | 'userId' | 'criadoEm'>, criarRevisoes?: boolean) => Promise<void>;
  updateSessao: (
    id: string,
    updates: { disciplinaId?: string; conteudoId?: string; tempoMin: number; data: Date; questoesCertas?: number; questoesErradas?: number; questoesBranco?: number; observacao?: string; tipoEstudo?: string; paginaInicio?: number; paginaFim?: number; videoUrl?: string; videoMinutoInicio?: number; videoMinutoFim?: number }
  ) => Promise<void>;
  deleteSessao: (id: string) => Promise<void>;
  marcarRevisao: (revisaoId: string) => Promise<void>;
  addTipoEstudo: (nome: string) => Promise<void>;
  getConteudosByDisciplina: (disciplinaId: string) => Conteudo[];
  getSessoesByDisciplina: (disciplinaId: string) => SessaoEstudo[];
  getSessoesHoje: () => SessaoEstudo[];
  getRevisoesPendentes: () => { revisao: Revisao; conteudo: Conteudo; disciplina: Disciplina }[];
  getRevisoesConcluidas: () => { revisao: Revisao; conteudo: Conteudo; disciplina: Disciplina }[];
  getRevisoesFuturas: () => { revisao: Revisao; conteudo: Conteudo; disciplina: Disciplina }[];
  getTodasRevisoes: () => { revisao: Revisao; conteudo: Conteudo; disciplina: Disciplina }[];
  getDisciplinaById: (id: string) => Disciplina | undefined;
  getConteudoById: (id: string) => Conteudo | undefined;
  getTipoEstudoNome: (id: string) => string;
  refreshData: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Helper functions to convert between DB and App types
const toAppDisciplina = (db: DbDisciplina): Disciplina => ({
  id: db.id,
  userId: db.user_id,
  nome: db.nome,
  sigla: db.sigla || undefined,
  ativo: db.ativo,
  tempoCicloMin: db.tempo_ciclo_min ?? 30,
  criadoEm: new Date(db.criado_em),
});

const toAppConteudo = (db: DbConteudo): Conteudo => ({
  id: db.id,
  disciplinaId: db.disciplina_id,
  nome: db.nome,
  descricao: db.descricao || undefined,
  revisaoConcluida: db.revisao_concluida,
  criadoEm: new Date(db.criado_em),
});

const toAppSessao = (db: DbSessaoEstudo): SessaoEstudo => ({
  id: db.id,
  userId: db.user_id,
  disciplinaId: db.disciplina_id,
  conteudoId: db.conteudo_id || undefined,
  tempoMin: db.tempo_min,
  data: parseDateOnly(db.data),
  questoesCertas: db.questoes_certas || undefined,
  questoesErradas: db.questoes_erradas || undefined,
  questoesBranco: db.questoes_branco || undefined,
  observacao: db.observacao || undefined,
  tipoEstudo: db.tipo_estudo,
  paginaInicio: db.pagina_inicio || undefined,
  paginaFim: db.pagina_fim || undefined,
  videoUrl: db.video_url || undefined,
  videoMinutoInicio: db.video_minuto_inicio ?? undefined,
  videoMinutoFim: db.video_minuto_fim ?? undefined,
  criadoEm: new Date(db.criado_em),
});

const toAppTipoEstudo = (db: DbTipoEstudo): TipoEstudo => ({
  id: db.id,
  nome: db.nome,
});

const toAppRevisao = (db: DbRevisao): Revisao => ({
  id: db.id,
  conteudoId: db.conteudo_id,
  concluida: db.concluida,
  dataConclusao: db.data_conclusao ? parseDateOnly(db.data_conclusao) : undefined,
  dataAgendada: parseDateOnly(db.data_agendada),
  sessaoId: db.sessao_id || undefined,
  criadoEm: new Date(db.criado_em),
});

const toAppSimulado = (db: DbSimulado): Simulado => ({
  id: db.id,
  userId: db.user_id,
  titulo: db.titulo,
  banca: db.banca,
  tipoQuestao: db.tipo_questao,
  tipoPontuacao: db.tipo_pontuacao,
  questoesCertas: db.questoes_certas,
  questoesErradas: db.questoes_erradas,
  questoesBranco: db.questoes_branco,
  notaFinal: db.nota_final,
  data: parseDateOnly(db.data),
  observacao: db.observacao,
  criadoEm: new Date(db.criado_em),
});

// Spaced repetition intervals in days: 1, 7, 14, 21, then every 21 days until ~1 year
const getSpacedRepetitionDates = (baseDate: Date): Date[] => {
  const dates: Date[] = [];
  const intervals = [1, 7, 14, 21]; // Initial intervals
  
  // Add first 4 intervals
  intervals.forEach(days => {
    const date = new Date(baseDate);
    date.setDate(date.getDate() + days);
    dates.push(date);
  });
  
  // Continue with 21-day intervals until ~1 year (365 days)
  let currentDays = 21;
  while (currentDays + 21 <= 365) {
    currentDays += 21;
    const date = new Date(baseDate);
    date.setDate(date.getDate() + currentDays);
    dates.push(date);
  }
  
  return dates;
};

export function AppProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [objetivoGeral, setObjetivoGeralState] = useState('');
  const [nomeCompleto, setNomeCompletoState] = useState('');
  const [disciplinas, setDisciplinas] = useState<Disciplina[]>([]);
  const [conteudos, setConteudos] = useState<Conteudo[]>([]);
  const [sessoes, setSessoes] = useState<SessaoEstudo[]>([]);
  const [revisoes, setRevisoes] = useState<Revisao[]>([]);
  const [simulados, setSimulados] = useState<Simulado[]>([]);
  const [tiposEstudo, setTiposEstudo] = useState<TipoEstudo[]>(DEFAULT_TIPOS_ESTUDO);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!user) {
      setDisciplinas([]);
      setConteudos([]);
      setSessoes([]);
      setRevisoes([]);
      setSimulados([]);
      setTiposEstudo(DEFAULT_TIPOS_ESTUDO);
      setObjetivoGeralState('');
      setNomeCompletoState('');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (profileData) {
        const profile = profileData as DbProfile;
        setObjetivoGeralState(profile.objetivo_geral || '');
        setNomeCompletoState(profile.nome_completo || '');
      }

      // Fetch disciplinas
      const { data: disciplinasData } = await supabase
        .from('disciplinas')
        .select('*')
        .eq('user_id', user.id)
        .order('criado_em', { ascending: false });
      
      if (disciplinasData) {
        setDisciplinas((disciplinasData as DbDisciplina[]).map(toAppDisciplina));
      }

      // Fetch conteudos (via disciplinas)
      const { data: conteudosData } = await supabase
        .from('conteudos')
        .select('*')
        .order('criado_em', { ascending: false });
      
      if (conteudosData) {
        setConteudos((conteudosData as DbConteudo[]).map(toAppConteudo));
      }

      // Fetch sessoes
      const { data: sessoesData } = await supabase
        .from('sessoes_estudo')
        .select('*')
        .eq('user_id', user.id)
        .order('data', { ascending: false });
      
      if (sessoesData) {
        setSessoes((sessoesData as DbSessaoEstudo[]).map(toAppSessao));
      }

      // Fetch revisoes
      const { data: revisoesData } = await supabase
        .from('revisoes')
        .select('*')
        .order('criado_em', { ascending: false });
      
      if (revisoesData) {
        setRevisoes((revisoesData as DbRevisao[]).map(toAppRevisao));
      }

      // Fetch simulados
      const { data: simuladosData } = await supabase
        .from('simulados')
        .select('*')
        .eq('user_id', user.id)
        .order('data', { ascending: false });
      
      if (simuladosData) {
        setSimulados((simuladosData as DbSimulado[]).map(toAppSimulado));
      }

      // Fetch custom tipos de estudo
      const { data: tiposEstudoData } = await supabase
        .from('tipos_estudo')
        .select('*')
        .eq('user_id', user.id)
        .order('criado_em', { ascending: true });
      
      if (tiposEstudoData) {
        setTiposEstudo([
          ...DEFAULT_TIPOS_ESTUDO,
          ...(tiposEstudoData as DbTipoEstudo[]).map(toAppTipoEstudo),
        ]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setObjetivoGeral = async (objetivo: string) => {
    if (!user) return;

    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (existingProfile) {
      await supabase
        .from('profiles')
        .update({ objetivo_geral: objetivo })
        .eq('user_id', user.id);
    } else {
      await supabase
        .from('profiles')
        .insert({ user_id: user.id, objetivo_geral: objetivo });
    }

    setObjetivoGeralState(objetivo);
  };

  const setNomeCompleto = async (nome: string) => {
    if (!user) return;

    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (existingProfile) {
      await supabase
        .from('profiles')
        .update({ nome_completo: nome })
        .eq('user_id', user.id);
    } else {
      await supabase
        .from('profiles')
        .insert({ user_id: user.id, nome_completo: nome });
    }

    setNomeCompletoState(nome);
  };

  const addDisciplina = async (nome: string, sigla?: string, tempoCicloMin?: number) => {
    if (!user) return;

    const { data, error } = await supabase
      .from('disciplinas')
      .insert({ user_id: user.id, nome, sigla: sigla || null, tempo_ciclo_min: tempoCicloMin ?? 30 } as any)
      .select()
      .single();

    if (data && !error) {
      setDisciplinas(prev => [toAppDisciplina(data as DbDisciplina), ...prev]);
    }
  };

  const updateDisciplina = async (id: string, nome: string, sigla?: string, tempoCicloMin?: number) => {
    const { error } = await supabase
      .from('disciplinas')
      .update({ nome, sigla: sigla || null, tempo_ciclo_min: tempoCicloMin ?? 30 } as any)
      .eq('id', id);

    if (!error) {
      setDisciplinas(prev =>
        prev.map(d => (d.id === id ? { ...d, nome, sigla, tempoCicloMin: tempoCicloMin ?? 30 } : d))
      );
    }
  };

  const deleteDisciplina = async (id: string) => {
    const { error } = await supabase
      .from('disciplinas')
      .delete()
      .eq('id', id);

    if (!error) {
      setDisciplinas(prev => prev.filter(d => d.id !== id));
      setConteudos(prev => prev.filter(c => c.disciplinaId !== id));
      setSessoes(prev => prev.filter(s => s.disciplinaId !== id));
    }
  };

  const toggleDisciplinaAtivo = async (id: string) => {
    const disciplina = disciplinas.find(d => d.id === id);
    if (!disciplina) return;

    const novoValor = !disciplina.ativo;
    const { error } = await supabase
      .from('disciplinas')
      .update({ ativo: novoValor } as any)
      .eq('id', id);

    if (!error) {
      setDisciplinas(prev =>
        prev.map(d => (d.id === id ? { ...d, ativo: novoValor } : d))
      );
    }
  };

  const addConteudo = async (disciplinaId: string, nome: string, descricao?: string) => {
    const { data, error } = await supabase
      .from('conteudos')
      .insert({ 
        disciplina_id: disciplinaId, 
        nome, 
        descricao: descricao || null 
      })
      .select()
      .single();

    if (data && !error) {
      setConteudos(prev => [toAppConteudo(data as DbConteudo), ...prev]);
    }
  };

  const updateConteudo = async (id: string, nome: string, descricao?: string) => {
    const { error } = await supabase
      .from('conteudos')
      .update({ nome, descricao: descricao || null })
      .eq('id', id);

    if (!error) {
      setConteudos(prev =>
        prev.map(c => (c.id === id ? { ...c, nome, descricao } : c))
      );
    }
  };

  const deleteConteudo = async (id: string) => {
    const { error } = await supabase
      .from('conteudos')
      .delete()
      .eq('id', id);

    if (!error) {
      setConteudos(prev => prev.filter(c => c.id !== id));
      setRevisoes(prev => prev.filter(r => r.conteudoId !== id));
    }
  };

  const toggleConteudoConcluido = async (id: string) => {
    const conteudo = conteudos.find((c) => c.id === id);
    if (!conteudo) return;

    const novoValor = !conteudo.revisaoConcluida;
    const { error } = await supabase
      .from('conteudos')
      .update({ revisao_concluida: novoValor })
      .eq('id', id);

    if (!error) {
      setConteudos((prev) =>
        prev.map((c) => (c.id === id ? { ...c, revisaoConcluida: novoValor } : c))
      );
    }
  };

  const addSessao = async (sessao: Omit<SessaoEstudo, 'id' | 'userId' | 'criadoEm'>, criarRevisoes: boolean = true) => {
    if (!user) return;

    const { data, error } = await supabase
      .from('sessoes_estudo')
      .insert({
        user_id: user.id,
        disciplina_id: sessao.disciplinaId,
        conteudo_id: sessao.conteudoId || null,
        tempo_min: sessao.tempoMin,
        // Use local date formatting to avoid timezone shifting.
        data: format(sessao.data, 'yyyy-MM-dd'),
        questoes_certas: sessao.questoesCertas ?? null,
        questoes_erradas: sessao.questoesErradas ?? null,
        questoes_branco: sessao.questoesBranco ?? null,
        observacao: sessao.observacao ?? null,
        tipo_estudo: sessao.tipoEstudo,
        pagina_inicio: sessao.paginaInicio ?? null,
        pagina_fim: sessao.paginaFim ?? null,
        video_url: sessao.videoUrl ?? null,
        video_minuto_inicio: sessao.videoMinutoInicio ?? null,
        video_minuto_fim: sessao.videoMinutoFim ?? null,
      })
      .select()
      .single();

    if (data && !error) {
      const sessaoData = data as DbSessaoEstudo;
      setSessoes(prev => [toAppSessao(sessaoData), ...prev]);

      // Create spaced repetition reviews if conteudo is selected and user wants to create reviews
      if (sessao.conteudoId && criarRevisoes) {
        const reviewDates = getSpacedRepetitionDates(sessao.data);

        const revisionsToInsert = reviewDates.map(date => ({
          conteudo_id: sessao.conteudoId!,
          sessao_id: sessaoData.id,
          data_agendada: format(date, 'yyyy-MM-dd'),
          concluida: false,
        }));

        const { data: revisoesData } = await supabase
          .from('revisoes')
          .insert(revisionsToInsert)
          .select();

        if (revisoesData) {
          setRevisoes(prev => [
            ...(revisoesData as DbRevisao[]).map(toAppRevisao),
            ...prev,
          ]);
        }
      }
    }
  };

  const updateSessao = async (
    id: string,
    updates: { disciplinaId?: string; conteudoId?: string; tempoMin: number; data: Date; questoesCertas?: number; questoesErradas?: number; questoesBranco?: number; observacao?: string; tipoEstudo?: string; paginaInicio?: number; paginaFim?: number; videoUrl?: string; videoMinutoInicio?: number; videoMinutoFim?: number }
  ) => {
    const updatePayload: Record<string, unknown> = {
      tempo_min: updates.tempoMin,
      data: format(updates.data, 'yyyy-MM-dd'),
      questoes_certas: updates.questoesCertas ?? null,
      questoes_erradas: updates.questoesErradas ?? null,
      questoes_branco: updates.questoesBranco ?? null,
      observacao: updates.observacao ?? null,
      tipo_estudo: updates.tipoEstudo ?? 'estudo_teorico',
      pagina_inicio: updates.paginaInicio ?? null,
      pagina_fim: updates.paginaFim ?? null,
      video_url: updates.videoUrl ?? null,
      video_minuto_inicio: updates.videoMinutoInicio ?? null,
      video_minuto_fim: updates.videoMinutoFim ?? null,
    };
    if (updates.disciplinaId) updatePayload.disciplina_id = updates.disciplinaId;
    if (updates.conteudoId) updatePayload.conteudo_id = updates.conteudoId;

    const { data, error } = await supabase
      .from('sessoes_estudo')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single();

    if (data && !error) {
      setSessoes(prev => prev.map(s => (s.id === id ? toAppSessao(data as DbSessaoEstudo) : s)));
    }
  };

  const deleteSessao = async (id: string) => {
    const { error } = await supabase
      .from('sessoes_estudo')
      .delete()
      .eq('id', id);

    if (!error) {
      setSessoes(prev => prev.filter(s => s.id !== id));
    }
  };

  const marcarRevisao = async (revisaoId: string) => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');

    const { error } = await supabase
      .from('revisoes')
      .update({
        concluida: true,
        data_conclusao: todayStr,
      })
      .eq('id', revisaoId);

    if (!error) {
      setRevisoes(prev =>
        prev.map(r =>
          r.id === revisaoId
            ? { ...r, concluida: true, dataConclusao: parseDateOnly(todayStr) }
            : r
        )
      );
    }
  };

  const getConteudosByDisciplina = (disciplinaId: string) => {
    return conteudos.filter(c => c.disciplinaId === disciplinaId);
  };

  const getSessoesByDisciplina = (disciplinaId: string) => {
    return sessoes.filter(s => s.disciplinaId === disciplinaId);
  };

  const getSessoesHoje = () => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return sessoes.filter(s => {
      const sessaoDate = new Date(s.data);
      sessaoDate.setHours(0, 0, 0, 0);
      return sessaoDate.getTime() === hoje.getTime();
    });
  };

  const getRevisoesPendentes = () => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    const pendentes = revisoes.filter(r => {
      const dataAgendada = new Date(r.dataAgendada);
      dataAgendada.setHours(0, 0, 0, 0);
      return !r.concluida && dataAgendada <= hoje;
    });
    
    return pendentes.map(revisao => {
      const conteudo = conteudos.find(c => c.id === revisao.conteudoId);
      const disciplina = conteudo ? disciplinas.find(d => d.id === conteudo.disciplinaId) : undefined;
      return { revisao, conteudo: conteudo!, disciplina: disciplina! };
    }).filter(item => item.conteudo && item.disciplina);
  };

  const getRevisoesConcluidas = () => {
    const concluidas = revisoes.filter(r => r.concluida);
    
    return concluidas.map(revisao => {
      const conteudo = conteudos.find(c => c.id === revisao.conteudoId);
      const disciplina = conteudo ? disciplinas.find(d => d.id === conteudo.disciplinaId) : undefined;
      return { revisao, conteudo: conteudo!, disciplina: disciplina! };
    }).filter(item => item.conteudo && item.disciplina);
  };

  const getRevisoesFuturas = () => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    const futuras = revisoes.filter(r => {
      const dataAgendada = new Date(r.dataAgendada);
      dataAgendada.setHours(0, 0, 0, 0);
      return !r.concluida && dataAgendada > hoje;
    });
    
    return futuras.map(revisao => {
      const conteudo = conteudos.find(c => c.id === revisao.conteudoId);
      const disciplina = conteudo ? disciplinas.find(d => d.id === conteudo.disciplinaId) : undefined;
      return { revisao, conteudo: conteudo!, disciplina: disciplina! };
    }).filter(item => item.conteudo && item.disciplina)
      .sort((a, b) => a.revisao.dataAgendada.getTime() - b.revisao.dataAgendada.getTime());
  };

  const getTodasRevisoes = () => {
    return revisoes.map(revisao => {
      const conteudo = conteudos.find(c => c.id === revisao.conteudoId);
      const disciplina = conteudo ? disciplinas.find(d => d.id === conteudo.disciplinaId) : undefined;
      return { revisao, conteudo: conteudo!, disciplina: disciplina! };
    }).filter(item => item.conteudo && item.disciplina);
  };

  const getDisciplinaById = (id: string) => {
    return disciplinas.find(d => d.id === id);
  };

  const getConteudoById = (id: string) => {
    return conteudos.find(c => c.id === id);
  };

  const addTipoEstudo = async (nome: string) => {
    if (!user) return;

    const { data, error } = await supabase
      .from('tipos_estudo')
      .insert({ user_id: user.id, nome })
      .select()
      .single();

    if (data && !error) {
      setTiposEstudo(prev => [...prev, toAppTipoEstudo(data as DbTipoEstudo)]);
    }
  };

  const getTipoEstudoNome = (id: string) => {
    const tipo = tiposEstudo.find(t => t.id === id);
    return tipo?.nome || 'Estudo Teórico';
  };

  return (
    <AppContext.Provider
      value={{
        objetivoGeral,
        nomeCompleto,
        disciplinas,
        conteudos,
        sessoes,
        revisoes,
        simulados,
        tiposEstudo,
        loading,
        setObjetivoGeral,
        setNomeCompleto,
        addDisciplina,
        updateDisciplina,
        deleteDisciplina,
        toggleDisciplinaAtivo,
        addConteudo,
        updateConteudo,
        deleteConteudo,
        toggleConteudoConcluido,
        addSessao,
        updateSessao,
        deleteSessao,
        marcarRevisao,
        addTipoEstudo,
        getConteudosByDisciplina,
        getSessoesByDisciplina,
        getSessoesHoje,
        getRevisoesPendentes,
        getRevisoesConcluidas,
        getRevisoesFuturas,
        getTodasRevisoes,
        getDisciplinaById,
        getConteudoById,
        getTipoEstudoNome,
        refreshData: fetchData,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
